import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  ArrowLeft, Check, Eye, Minus, Package, Pencil, Plus, X,
} from 'lucide-react';

import { useXomicsAuth } from './AuthContext';
import {
  createBundle, loadBundles, loadOfficeStore, loadProducts, loadTranslationCatalogue, loadTranslations, saveTranslation,
  setBundleOffered, updateBundle,
} from './api';
import {
  Empty, Failed, Head, Loading, SearchField,
} from './parts';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import { Switch } from './ProductOptions';
import ProductPageLive from './ProductPageLive';
import { StoreOfficeLine, useStoreOffice } from './StoreOffice';
import './productDetail.css';
import './bundles.css';

// Bundles: products that cost less together, or more of one product for
// less. The bag takes the saving off by itself once it holds the bundle;
// nobody types a code, and this page has nothing to do with coupons.
//
// One rule decides where a bundle is seen: it shows on the page of every
// product in it. So the page draws each bundle the way a customer meets it
// (the products' photos, what they cost and what they cost together), names
// the pages it is on, and opens any of them here, as it really is.
//
// A bundle is made in three choices: what kind, which products, how much
// off. What is being made shows beside them the way the storefront will draw
// it. A bundle's line on the product pages is a key in the storefront's site
// text (bundle.noteKey), which XOmics makes with the bundle; the English is
// read and written here, the other languages in Translations.

const SITE_TEXT = 'storefront_site';
const MAX_TOGETHER = 4;
const MAX_QUANTITY = 99;
// The savings offered as buttons; any other percent is typed.
const PERCENTS = ['5', '10', '15', '20', '25'];
// Prices here are the store's euro prices, as a guide to what a bundle does.
const CURRENCY = 'EUR';

let siteTextType = null;
function siteTextTypeId() {
  siteTextType = siteTextType || loadTranslationCatalogue()
    .then((result) => result.contentTypes.find((entry) => entry.code === SITE_TEXT)?.id ?? null)
    .catch((error) => { siteTextType = null; throw error; });
  return siteTextType;
}

// The bundle's line as its translation row, or null when it has none.
async function readNote(key) {
  const contentTypeId = await siteTextTypeId();
  if (contentTypeId === null) return null;
  const { translations } = await loadTranslations({ contentTypeId, languageCode: 'en', q: key, limit: 5 });
  return translations.find((row) => row.translationKey === key) || null;
}

// The two kinds this page makes: different products bought together (one of
// each), or a number of one product.
const kindOf = (bundle) => (bundle.items.length === 1 ? 'quantity' : 'together');
const simple = (bundle) => bundle.items.length === 1 || bundle.items.every((item) => item.quantity === 1);
const percentOf = (basisPoints) => String(Math.round(Number(basisPoints)) / 100);

function money(minor) {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: CURRENCY }).format(minor / 100);
}

function priceMinor(product) {
  const price = product?.prices?.find((entry) => entry.currency === CURRENCY && entry.isActive);
  return price ? price.amountMinor : null;
}

// What the products cost, what the bundle takes off (the way the bag works it
// out) and what is left; null when one of them has no euro price.
function pricing(items, basisPoints, byId) {
  let normalMinor = 0;
  for (const item of items) {
    const each = priceMinor(byId.get(item.productId));
    if (each === null) return null;
    normalMinor += each * item.quantity;
  }
  const savingMinor = Math.floor((normalMinor * basisPoints) / 10000);
  return { normalMinor, savingMinor, bundleMinor: normalMinor - savingMinor };
}

// The bundle said by its products: "NAD+ + Gly-NAC", "2 × Gut Microbiome Test".
function titleOf(items, byId) {
  const name = (item) => byId.get(item.productId)?.name ?? tx('ui.bundles.unknown_product');
  if (items.length === 1) return `${items[0].quantity} × ${name(items[0])}`;
  return items.map(name).join(' + ');
}

// The name somebody gave the bundle, when it is more than its products said
// again in some order.
function ownName(bundle, byId) {
  const said = (text) => text.split(' + ').map((part) => part.trim()).sort().join(' + ');
  return said(bundle.name) === said(titleOf(bundle.items, byId)) ? '' : bundle.name;
}

function Photo({ product }) {
  return <span className="xo-bd-photo">
    {product?.imageUrl ? <img src={product.imageUrl} alt="" loading="lazy" /> : <Package aria-hidden="true" />}
  </span>;
}

// The products of a bundle as a row of photos: a plus between different
// products, the number on one product bought several times.
function Art({ items, byId }) {
  if (items.length === 1) {
    return <span className="xo-bd-art">
      <Photo product={byId.get(items[0].productId)} />
      <b className="xo-bd-times">× {items[0].quantity}</b>
    </span>;
  }
  return <span className="xo-bd-art">
    {items.map((item, index) => <React.Fragment key={item.productId}>
      {index > 0 && <i className="xo-bd-plus" aria-hidden="true"><Plus /></i>}
      <Photo product={byId.get(item.productId)} />
    </React.Fragment>)}
  </span>;
}

function BundleCard({
  bundle, isActive, byId, mayManage, busy, locked, onToggle, onEdit, onView,
}) {
  const title = titleOf(bundle.items, byId);
  const percent = percentOf(bundle.discountBasisPoints);
  const price = pricing(bundle.items, bundle.discountBasisPoints, byId);
  const pages = bundle.items.map((item) => byId.get(item.productId)).filter((product) => product?.path);
  return <li className={`xo-bd-card${isActive ? '' : ' is-off'}`} aria-labelledby={`xo-bd-card-${bundle.id}`}>
    <div className="xo-bd-card-art"><Art items={bundle.items} byId={byId} /></div>
    <strong className="xo-bd-card-title" id={`xo-bd-card-${bundle.id}`}>{title}</strong>
    {ownName(bundle, byId) && <span className="xo-bd-card-name">{ownName(bundle, byId)}</span>}
    <p className="xo-bd-card-price">
      {price
        ? <>
          <s aria-label={tx('ui.bundles.normal_price', { price: money(price.normalMinor) })}>{money(price.normalMinor)}</s>
          <b aria-label={tx('ui.bundles.bundle_price', { price: money(price.bundleMinor) })}>{money(price.bundleMinor)}</b>
        </>
        : <span className="xo-bd-card-missing">{tx('ui.bundles.no_price')}</span>}
      <span className="xo-bd-badge">{tx('ui.bundles.percent_off', { percent })}</span>
    </p>
    <div className="xo-bd-card-where">
      <span>{isActive ? tx('ui.bundles.shows_on') : tx('ui.bundles.shows_nowhere')}</span>
      {isActive && pages.map((product) => <button type="button" key={product.id} className="xo-bd-chip"
        aria-label={tx('ui.bundles.see_page', { product: product.name })} onClick={() => onView(product)}>
        <Eye aria-hidden="true" />{product.name}</button>)}
    </div>
    <div className="xo-bd-card-foot">
      <span className="xo-bd-card-switch">
        <Switch on={isActive} disabled={!mayManage || busy || locked} label={tx('ui.bundles.switch', { name: title })}
          onChange={(value) => onToggle(bundle, value)} />
        <span>{isActive ? tx('ui.bundles.on') : tx('ui.bundles.off')}</span>
      </span>
      <span className="xo-bd-card-used">{bundle.usedCount > 0
        ? tx('ui.bundles.used', { count: bundle.usedCount }) : tx('ui.bundles.used_never')}</span>
      {mayManage && simple(bundle) && <button type="button" className="xo-btn-2" disabled={locked}
        aria-label={tx('ui.bundles.edit_of', { name: title })} onClick={() => onEdit(bundle)}>
        <Pencil aria-hidden="true" />{tx('ui.bundles.edit')}</button>}
    </div>
  </li>;
}

// The bundle as the storefront draws it: products bought together as the
// "Frequently bought together" block, more of one product as the choice in
// that product's buy box.
function ShopPicture({
  kind, items, percent, price, note, byId,
}) {
  if (items.length === 0 || (kind === 'together' && items.length < 2)) {
    return <p className="xo-bd-shop-empty">{tx('ui.bundles.preview_empty')}</p>;
  }
  if (kind === 'quantity') {
    const product = byId.get(items[0].productId);
    const each = priceMinor(product);
    return <div className="xo-bd-shop is-choice">
      <div className="xo-bd-shop-product"><Photo product={product} /><strong>{product?.name}</strong></div>
      {note && <p className="xo-bd-shop-note">{note}</p>}
      <div className="xo-bd-shop-opt"><i aria-hidden="true" /><span>{tx('ui.bundles.shop_buy_count', { count: 1 })}</span>
        <b>{each === null ? '' : money(each)}</b></div>
      <div className="xo-bd-shop-opt is-on"><i aria-hidden="true" />
        <span>{tx('ui.bundles.shop_buy_count', { count: items[0].quantity })}<em>{tx('ui.bundles.shop_badge', { percent })}</em></span>
        <b>{price ? money(price.bundleMinor) : ''}</b></div>
    </div>;
  }
  return <div className="xo-bd-shop">
    <p className="xo-bd-shop-title"><span>{tx('ui.bundles.shop_title_lead')}</span>{tx('ui.bundles.shop_title_rest')}</p>
    {note && <p className="xo-bd-shop-note">{note}</p>}
    <ul className="xo-bd-shop-tray">
      {items.map((item, index) => {
        const product = byId.get(item.productId);
        const each = priceMinor(product);
        return <li className="xo-bd-shop-item" key={item.productId}>
          <span className="xo-bd-shop-ph">
            {index > 0 && <i className="xo-bd-shop-plus" aria-hidden="true"><Plus /></i>}
            <Photo product={product} />
          </span>
          <strong>{product?.name}</strong>
          <span>{each === null ? '' : money(each)}</span>
        </li>;
      })}
    </ul>
    <div className="xo-bd-shop-buy">
      <span>{items.length === 2 ? tx('ui.bundles.shop_buy_two') : tx('ui.bundles.shop_buy_many', { count: items.length })}</span>
      <strong>{price ? money(price.normalMinor) : ''}</strong>
      {price && <em>{tx('ui.bundles.shop_save', { percent, price: money(price.bundleMinor) })}</em>}
      <b>{items.length === 2 ? tx('ui.bundles.shop_add_two') : tx('ui.bundles.shop_add_many')}</b>
    </div>
  </div>;
}

function formOf(bundle) {
  if (!bundle) {
    return {
      name: '', kind: 'together', picked: [], quantity: 2, percent: '10', isActive: true,
    };
  }
  const single = kindOf(bundle) === 'quantity';
  return {
    name: bundle.name,
    kind: single ? 'quantity' : 'together',
    picked: bundle.items.map((item) => item.productId),
    quantity: single ? bundle.items[0].quantity : 2,
    percent: percentOf(bundle.discountBasisPoints),
    isActive: bundle.isActive,
  };
}

const itemsOf = (form) => (form.kind === 'quantity'
  ? form.picked.slice(0, 1).map((productId) => ({ productId, quantity: form.quantity }))
  : form.picked.map((productId) => ({ productId, quantity: 1 })));

const basisPointsOf = (form) => {
  const percent = Number(String(form.percent).replace(',', '.'));
  const basisPoints = Math.round(percent * 100);
  return Number.isFinite(percent) && basisPoints >= 1 && basisPoints <= 9900 ? basisPoints : null;
};

// The bundle as XOmics takes it (Bundles.js readBundleBody). Throws the
// message the form shows. A bundle without a name of its own is named after
// its products.
function bodyOf(form, byId) {
  const items = itemsOf(form);
  if (form.kind === 'together' && items.length < 2) throw new Error(tx('ui.bundles.error.together'));
  if (form.kind === 'quantity' && (items.length !== 1 || items[0].quantity < 2)) throw new Error(tx('ui.bundles.error.quantity'));
  const discountBasisPoints = basisPointsOf(form);
  if (discountBasisPoints === null) throw new Error(tx('ui.bundles.error.saving'));
  return {
    name: form.name.trim() || titleOf(items, byId), discountBasisPoints, isActive: form.isActive, items,
  };
}

function Step({
  number, title, aside, children,
}) {
  return <section className="xo-bd-step">
    <h3><span className="xo-bd-step-n" aria-hidden="true">{number}</span>{title}{aside && <small>{aside}</small>}</h3>
    {children}
  </section>;
}

// `office`: the office whose store the page is open on. A new bundle is
// offered by it from the start. `mayChooseAny`: the account manages every
// office's store, and sees the bundle's own switch, which is for all of them.
function BundleBuilder({
  bundle, products, office, mayChooseAny, mayEditText, onCancel, onSaved,
}) {
  const byId = useMemo(() => new Map(products.map((product) => [product.id, product])), [products]);
  const [form, setForm] = useState(() => formOf(bundle));
  const [note, setNote] = useState({ row: null, stored: '', value: '' });
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  useEffect(() => {
    if (!bundle) return undefined;
    let live = true;
    readNote(bundle.noteKey).then((row) => {
      if (live) setNote({ row, stored: row?.value ?? '', value: row?.value ?? '' });
    }).catch(() => {});
    return () => { live = false; };
  }, [bundle]);

  const together = form.kind === 'together';
  const full = together && form.picked.length >= MAX_TOGETHER;
  const items = itemsOf(form);
  const basisPoints = basisPointsOf(form);
  const price = basisPoints === null ? null : pricing(items, basisPoints, byId);

  // The products on sale, and any the bundle already holds, by category.
  const groups = useMemo(() => {
    const wanted = search.trim().toLowerCase();
    const shown = products.filter((product) => ((product.isActive && product.isSold !== false) || form.picked.includes(product.id))
      && (!wanted || product.name.toLowerCase().includes(wanted)));
    const byCategory = new Map();
    shown.forEach((product) => {
      const key = product.category?.name || '';
      if (!byCategory.has(key)) byCategory.set(key, []);
      byCategory.get(key).push(product);
    });
    return [...byCategory].map(([name, list]) => [name, list.sort((left, right) => left.name.localeCompare(right.name))])
      .sort((left, right) => right[1].length - left[1].length);
  }, [products, search, form.picked]);

  const setKind = (kind) => setForm((current) => ({
    ...current, kind, picked: kind === 'quantity' ? current.picked.slice(0, 1) : current.picked,
  }));
  const pick = (product) => setForm((current) => {
    const has = current.picked.includes(product.id);
    if (current.kind === 'quantity') return { ...current, picked: has ? [] : [product.id] };
    if (has) return { ...current, picked: current.picked.filter((id) => id !== product.id) };
    return current.picked.length >= MAX_TOGETHER ? current : { ...current, picked: [...current.picked, product.id] };
  });

  async function submit(event) {
    event.preventDefault();
    setError('');
    let details;
    try {
      details = bodyOf(form, byId);
    } catch (invalid) {
      setError(invalid.message);
      return;
    }
    setBusy(true);
    try {
      const saved = bundle ? await updateBundle(bundle.id, details)
        : await createBundle({ ...details, officeId: office?.id ?? null });
      // The line is written once XOmics has made its key with the bundle.
      if (mayEditText && note.value.trim() !== note.stored.trim()) {
        const row = note.row || await readNote(bundle ? bundle.noteKey : saved.bundle.noteKey);
        if (row) await saveTranslation(row.id, note.value.trim());
      }
      onSaved(bundle ? tx('ui.bundles.updated') : tx('ui.bundles.created'));
    } catch (requestError) {
      setError(errorMessage(requestError));
      setBusy(false);
    }
  }

  const typedPercent = PERCENTS.includes(form.percent) ? '' : form.percent;
  return <form className="xo-bd-builder" onSubmit={submit} aria-labelledby="xo-bd-builder-title">
    <button type="button" className="xo-link xo-bd-back" disabled={busy} onClick={onCancel}>
      <ArrowLeft aria-hidden="true" />{tx('ui.bundles.back')}</button>
    <h2 id="xo-bd-builder-title">{bundle ? tx('ui.bundles.change_title') : tx('ui.bundles.make_title')}</h2>
    <div className="xo-bd-builder-cols">
      <div className="xo-bd-steps">
        <Step number="1" title={tx('ui.bundles.kind_question')}>
          <div className="xo-bd-kinds" role="radiogroup" aria-label={tx('ui.bundles.kind_question')}>
            {[['together', 'ui.bundles.kind_together_line', 'ui.bundles.kind_together_example'],
              ['quantity', 'ui.bundles.kind_quantity_line', 'ui.bundles.kind_quantity_example']].map(([kind, line, example]) => (
              <button type="button" key={kind} role="radio" aria-checked={form.kind === kind} disabled={busy}
                className={form.kind === kind ? 'is-on' : ''} onClick={() => setKind(kind)}>
                <span className="xo-bd-kind-art" aria-hidden="true">
                  {kind === 'together'
                    ? <><i /><Plus /><i /></>
                    : <><i /><b>× 2</b></>}
                </span>
                <strong>{tx(line)}</strong>
                <small>{tx(example)}</small>
                {form.kind === kind && <span className="xo-bd-tick" aria-hidden="true"><Check /></span>}
              </button>
            ))}
          </div>
        </Step>

        <Step number="2" title={together ? tx('ui.bundles.pick_together') : tx('ui.bundles.pick_one')}
          aside={together && form.picked.length > 0 ? tx('ui.bundles.picked', { count: form.picked.length }) : ''}>
          <SearchField value={search} onChange={setSearch} placeholder={tx('ui.bundles.search')} className="xo-bd-search" />
          {groups.length === 0 && <p className="xo-bd-none">{tx('ui.bundles.no_match')}</p>}
          {groups.map(([category, list]) => <div className="xo-bd-group" key={category}>
            {groups.length > 1 && <h4>{category}</h4>}
            <ul className="xo-bd-picker">
              {list.map((product) => {
                const on = form.picked.includes(product.id);
                const blocked = product.hasOptions || (full && !on);
                const each = priceMinor(product);
                return <li key={product.id}>
                  <button type="button" role={together ? 'checkbox' : 'radio'} aria-checked={on}
                    disabled={busy || blocked} className={on ? 'is-on' : ''} onClick={() => pick(product)}>
                    <Photo product={product} />
                    <strong>{product.name}</strong>
                    <small>{product.hasOptions ? tx('ui.bundles.has_choices') : each === null ? '' : money(each)}</small>
                    {on && <span className="xo-bd-tick" aria-hidden="true"><Check /></span>}
                  </button>
                </li>;
              })}
            </ul>
          </div>)}
        </Step>

        <Step number="3" title={together ? tx('ui.bundles.how_much') : tx('ui.bundles.how_many_much')}>
          {!together && <div className="xo-bd-count">
            <span id="xo-bd-count-label">{tx('ui.bundles.how_many')}</span>
            <div role="group" aria-labelledby="xo-bd-count-label">
              <button type="button" disabled={busy || form.quantity <= 2} aria-label={tx('ui.bundles.fewer')}
                onClick={() => set('quantity', form.quantity - 1)}><Minus aria-hidden="true" /></button>
              <output aria-live="polite">{form.quantity}</output>
              <button type="button" disabled={busy || form.quantity >= MAX_QUANTITY} aria-label={tx('ui.bundles.more')}
                onClick={() => set('quantity', form.quantity + 1)}><Plus aria-hidden="true" /></button>
            </div>
          </div>}
          <div className="xo-bd-percents" role="radiogroup" aria-label={tx('ui.bundles.how_much')}>
            {PERCENTS.map((percent) => <button type="button" key={percent} role="radio" disabled={busy}
              aria-checked={form.percent === percent} className={form.percent === percent ? 'is-on' : ''}
              onClick={() => set('percent', percent)}>{percent}%</button>)}
            <label className={`xo-bd-other${typedPercent ? ' is-on' : ''}`}>
              <span>{tx('ui.bundles.other')}</span>
              <input type="text" inputMode="decimal" value={typedPercent} disabled={busy}
                aria-label={tx('ui.bundles.other_percent')} onChange={(event) => set('percent', event.target.value)} />
              <span aria-hidden="true">%</span>
            </label>
          </div>
        </Step>

        <section className="xo-bd-step is-optional">
          <h3>{tx('ui.bundles.extras')}<small>{tx('ui.bundles.optional')}</small></h3>
          <div className="xo-bd-field">
            <label htmlFor="xo-bd-line">{tx('ui.bundles.line_question')}</label>
            <input id="xo-bd-line" type="text" value={note.value} maxLength="255" dir="auto" disabled={busy || !mayEditText}
              placeholder={tx('ui.bundles.line_example')} aria-describedby="xo-bd-line-help"
              onChange={(event) => setNote((current) => ({ ...current, value: event.target.value }))} />
            <small id="xo-bd-line-help">{mayEditText ? tx('ui.bundles.line_help') : tx('ui.bundles.note_locked')}</small>
          </div>
          <div className="xo-bd-field">
            <label htmlFor="xo-bd-name">{tx('ui.bundles.name_question')}</label>
            <input id="xo-bd-name" type="text" value={form.name} maxLength="255" disabled={busy}
              placeholder={items.length > 0 ? titleOf(items, byId) : ''} aria-describedby="xo-bd-name-help"
              onChange={(event) => set('name', event.target.value)} />
            <small id="xo-bd-name-help">{tx('ui.bundles.name_help')}</small>
          </div>
        </section>
      </div>

      <aside className="xo-bd-side">
        <div className="xo-bd-side-head">
          <strong>{tx('ui.bundles.preview_title')}</strong>
          <small>{together ? tx('ui.bundles.preview_where_together') : tx('ui.bundles.preview_where_quantity')}</small>
        </div>
        <ShopPicture kind={form.kind} items={items} percent={basisPoints === null ? '' : percentOf(basisPoints)}
          price={price} note={note.value.trim()} byId={byId} />
        {price && items.length > 0 && (!together || items.length > 1) && <p className="xo-bd-math">
          {tx('ui.bundles.preview_math', {
            normal: money(price.normalMinor), bundle: money(price.bundleMinor), saving: money(price.savingMinor),
          })}</p>}
        {/* The bundle's own switch is for every office at once; each office's is on its card. */}
        {bundle && mayChooseAny && <div className="xo-bd-live">
          <span>
            <strong>{form.isActive ? tx('ui.bundles.on') : tx('ui.bundles.off')}</strong>
            <small>{tx('ui.store.everywhere')}</small>
          </span>
          <Switch on={form.isActive} disabled={busy} label={tx('ui.bundles.on')} onChange={(value) => set('isActive', value)} />
        </div>}
        {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
        <div className="xo-bd-actions">
          <button type="button" className="xo-btn-2" disabled={busy} onClick={onCancel}>{tx('ui.bundles.cancel')}</button>
          <button type="submit" className="xo-btn" disabled={busy}>
            {busy ? tx('ui.bundles.saving') : tx('ui.bundles.save_bundle')}</button>
        </div>
      </aside>
    </div>
  </form>;
}

export default function XomicsBundles() {
  const { permissions } = useXomicsAuth();
  const mayManage = permissions.includes('PRODUCT_MANAGE');
  const mayEditText = permissions.includes('CONTENT_MANAGE');
  // The office whose store the page is open on: a bundle's switch says whether
  // that office offers it, and the prices beside it are that office's.
  const store = useStoreOffice();
  const officeId = store.office?.id ?? null;
  const ready = !store.loading && !store.error;
  const products = useLiveResource(['products'], useCallback(() => loadProducts(officeId), [officeId]), { enabled: ready });
  const list = useLiveResource(['coupons'], useCallback(() => loadBundles(), []));
  const own = useLiveResource(['coupons'], useCallback(
    () => (officeId === null ? { bundles: [] } : loadOfficeStore(officeId)), [officeId],
  ), { enabled: ready });
  // undefined: the list; null: a new bundle; a bundle: that bundle being changed.
  const [editing, setEditing] = useState(undefined);
  // The product whose real page is open beside the list.
  const [viewing, setViewing] = useState(null);
  const [notice, setNotice] = useState(null);
  // A switch shows where it was put while XOmics is told.
  const [switching, setSwitching] = useState(null);
  const bundles = useMemo(() => list.data ?? [], [list.data]);
  const all = useMemo(() => products.data ?? [], [products.data]);
  const byId = useMemo(() => new Map(all.map((product) => [product.id, product])), [all]);
  // The bundles the office offers.
  const offered = useMemo(
    () => new Set((own.data?.bundles ?? []).filter((entry) => entry.isOffered).map((entry) => entry.bundleId)),
    [own.data],
  );

  if (store.error) return <Failed message={store.error} onRetry={store.refresh} />;
  if (store.loading || (products.data === null && !products.error) || (own.data === null && !own.error)
      || (list.loading && list.data === null)) {
    return <Loading label={tx('ui.bundles.loading')} />;
  }
  if (products.error) return <Failed message={products.error} onRetry={products.refresh} />;
  if (own.error) return <Failed message={own.error} onRetry={own.refresh} />;
  if (list.error) return <Failed message={list.error} onRetry={list.refresh} />;

  // On or off in this office's store.
  async function toggle(bundle, isActive) {
    setNotice(null);
    setSwitching({ id: bundle.id, isActive });
    try {
      await setBundleOffered(officeId, bundle.id, isActive);
      await Promise.all([list.refresh(), own.refresh()]);
      setNotice({
        tone: 'ok',
        text: tx(isActive ? 'ui.bundles.switched_on' : 'ui.bundles.switched_off', { name: titleOf(bundle.items, byId) }),
      });
    } catch (requestError) {
      setNotice({ tone: 'bad', text: errorMessage(requestError) });
    } finally {
      setSwitching(null);
    }
  }

  if (editing !== undefined) {
    return <div className="xo-platform-page xo-bd-page">
      <BundleBuilder key={editing?.id ?? 'new'} bundle={editing} products={all} office={store.office}
        mayChooseAny={store.canChooseAny} mayEditText={mayEditText}
        onCancel={() => setEditing(undefined)}
        onSaved={(text) => { setEditing(undefined); setNotice({ tone: 'ok', text }); list.refresh(); own.refresh(); }} />
    </div>;
  }

  const groups = [
    ['together', tx('ui.bundles.group_together'), bundles.filter((bundle) => kindOf(bundle) === 'together')],
    ['quantity', tx('ui.bundles.group_quantity'), bundles.filter((bundle) => kindOf(bundle) === 'quantity')],
  ].filter(([, , rows]) => rows.length > 0);
  // On for a visitor of this office: the bundle is switched on and the office offers it.
  const activeOf = (bundle) => (switching?.id === bundle.id ? switching.isActive : bundle.isActive && offered.has(bundle.id));

  return <div className="xo-platform-page xo-bd-page">
    <Head title={tx('ui.bundles.title')} under={<StoreOfficeLine store={store} />}>
      {mayManage && <button type="button" className="xo-btn" onClick={() => { setNotice(null); setViewing(null); setEditing(null); }}>
        <Plus aria-hidden="true" />{tx('ui.bundles.new')}</button>}
    </Head>
    {notice && <p className={`xo-alert t-${notice.tone}`} role={notice.tone === 'bad' ? 'alert' : 'status'}>
      {notice.tone === 'ok' && <Check aria-hidden="true" />}{notice.text}</p>}
    {bundles.length === 0 && <Empty title={tx('ui.bundles.empty')} detail={tx('ui.bundles.empty_detail')} />}
    <div className={`xo-bd-layout${viewing ? ' has-viewer' : ''}`}>
      <div className="xo-bd-groups">
        {groups.map(([kind, title, rows]) => <section className="xo-bd-list" key={kind} aria-labelledby={`xo-bd-${kind}`}>
          <h2 id={`xo-bd-${kind}`}>{title}
            <small>{tx('ui.bundles.count_on', { on: rows.filter((bundle) => activeOf(bundle)).length, total: rows.length })}</small></h2>
          <ul className="xo-bd-grid">
            {rows.map((bundle) => <BundleCard key={bundle.id} bundle={bundle} isActive={activeOf(bundle)} byId={byId}
              mayManage={mayManage && officeId !== null} busy={switching?.id === bundle.id} locked={switching !== null}
              onToggle={toggle} onEdit={(chosen) => { setNotice(null); setViewing(null); setEditing(chosen); }}
              onView={setViewing} />)}
          </ul>
        </section>)}
      </div>
      {viewing && <div className="xo-bd-viewer">
        <button type="button" className="xo-icon-btn xo-bd-viewer-close" aria-label={tx('ui.bundles.close_page')}
          onClick={() => setViewing(null)}><X aria-hidden="true" /></button>
        <ProductPageLive key={viewing.id} path={viewing.path} hash="bought-together" title={viewing.name}
          hint={tx('ui.bundles.real_page')} onPick={() => {}} onPickPhoto={() => {}} />
      </div>}
    </div>
  </div>;
}
